import { sendGet, sendPost, sendPut } from './generic';
import IResult from '../../models/result/IResult';
import { getAnalyticsApiUrl } from '../../constants/Urls';
import HistogramModel from '../../models/analytics/HistogramModel';

export interface DeepAnalysisUpsellValueConfig {
    dollarValue: number;
    displayName: string;
    hidden?: boolean;
    isHidden?: boolean;
}

export type DeepAnalysisUpsellValuesByTag = Record<string, DeepAnalysisUpsellValueConfig>;

export const syncDeepAnalysisUpsellValues = (organizationId: string, groupId?: string): Promise<IResult<DeepAnalysisUpsellValuesByTag>> => {
    const query = `organizationId=${encodeURIComponent(organizationId)}${groupId ? `&groupId=${encodeURIComponent(groupId)}` : ''}`;
    return sendPost<DeepAnalysisUpsellValuesByTag>(`/DeepAnalysis/UpsellValues/Sync?${query}`, {});
};

export const updateDeepAnalysisUpsellValues = (organizationId: string, values: DeepAnalysisUpsellValuesByTag): Promise<IResult<DeepAnalysisUpsellValuesByTag>> => {
    return sendPut<DeepAnalysisUpsellValuesByTag>(`/DeepAnalysis/UpsellValues/${encodeURIComponent(organizationId)}`, values);
};

// ==================== ENGAGEMENT ANALYTICS MODELS ====================

/**
 * App-level breakdown for metrics (used in tooltips and detailed views)
 */
export interface AppBreakdown {
    applicationId: string;
    applicationName: string;
    count: number;
    english?: number;
    spanish?: number;
}

/**
 * Individual engagement/call record from the organization
 * Matches the actual API response from getOrganizationEngagements and getEngagementsForTagCombo
 */
export interface OrganizationEngagement {
    // Core identifiers
    id: string;
    organizationId: string;
    applicationId: string;
    sessionId: string;
    requestId: string;
    nativeUserId?: string;
    featureFlagId?: string;

    // Event metadata
    eventType: string;  // e.g., 'EngagementEvent'
    eventDate: string;  // ISO date string

    // Session categorization
    sessionCategory: string;  // e.g., 'ACTION_ATTEMPTED', 'IMMEDIATE_TRANSFER', 'OTHER', 'NO_INTERACTION'
    sessionTags: string[];    // e.g., ['ACTION_COMPLETED', 'NEW_PATIENT', 'SCHEDULE_ATTEMPTED']
    sessionSummaries?: string; // JSON string of session summaries

    // Containment info
    isContainable: boolean;
    containableReason?: string;
    isContained: boolean;
    containmentReason?: string;

    // Transfer info
    isPlannedTransfer: boolean;
    plannedTransferReason: string;
    isUnplannedTransfer: boolean;
    unplannedTransferReason: string;

    // Duration metrics
    preTransferDurationMinutes: number;
    postTransferDurationMinutes: number;

    // Food Ordering Money tracking
    upsoldItems: string[];
    orderTotal: number;

    // Legacy/optional fields for backward compatibility
    applicationName?: string;
    startDate?: string;
    endDate?: string;
    date?: string;
    time?: string;
    phoneLastFour?: string;
    transcriptLink?: string;
    category?: string;  // Legacy - use sessionCategory
    tags?: string[];    // Legacy - use sessionTags
    location?: string;
    isSpanish?: boolean;
    language?: string;
}

/**
 * Engagements grouped by applicationId
 * This is the actual response format from getOrganizationEngagements and getEngagementsForTagCombo endpoints
 */
export type GroupedEngagements = Record<string, OrganizationEngagement[]>;
export type GroupedTopLevelMetrics = Record<string, TopLevelMetrics>;

/**
 * Individual histogram entry from organization-level histogram endpoints
 */
export interface HistogramEntry {
    key: string;
    date: string;
    count: number;
}

/**
 * Histogram data grouped by applicationId
 * This is the actual response format from organization-level histogram endpoints like
 * getOrganizationCallVolumeHistogram, getOrganizationContainableCallVolumeHistogram, etc.
 */
export type GroupedHistogramData = Record<string, HistogramEntry[]>;

/**
 * Top-level metrics for KPI tiles
 * Maps to mockHealthcareMetrics structure
 */
export interface TopLevelMetrics {
    // Call counts
    totalCalls?: number;
    containedCalls?: number;
    containableCalls?: number;
    transferredCalls?: number;
    immediateTransfers?: number;
    // Rates
    containmentRate?: number;
    transferRate?: number;
    // Healthcare-specific metrics
    laborMinutesSaved?: number;  // Raw minutes from API
    laborHoursSaved?: number;    // Computed: laborMinutesSaved / 60
    offHoursCalls?: number;
    newPatientBookings?: number;
    existingPatientBookings?: number;
    totalBookings?: number;
    // Food ordering-specific metrics
    orderAttempts?: number;
    ordersPlaced?: number;
    totalAiIssueOrderAttempts?: number;
    totalAiIssueOrderAttemptsEnglish?: number;
    totalAiIssueOrderAttemptsSpanish?: number;
    totalAiIssueFailedOrderAttempts?: number;
    totalAiIssueFailedOrderAttemptsEnglish?: number;
    totalAiIssueFailedOrderAttemptsSpanish?: number;
    upsellAttempts?: number;
    upsellSuccesses?: number;
    averageOrderTotal?: number;
    totalSold?: number;
    questionsAnswered?: number;
    // Food ordering Spanish-specific metrics
    averageTicketSizeSpanish?: number;
    averageTicketSizeEnglish?: number;
    totalOrdersCompletedSpanish?: number;
    totalOrdersCompletedEnglish?: number;
    totalQuestionsAnsweredSpanish?: number;
    totalQuestionsAnsweredEnglish?: number;
    totalUpsellAttemptsSpanish?: number;
    totalUpsellAttemptsEnglish?: number;
    totalUpsellsCompletedSpanish?: number;
    totalUpsellsCompletedEnglish?: number;
    // App-level breakdowns
    laborHoursSavedApps?: AppBreakdown[];
    offHoursApps?: AppBreakdown[];
    offHoursScheduledAppts?: number;
    offHoursScheduledApptsApps?: AppBreakdown[];
    newPatientBookingsApps?: AppBreakdown[];
    containedCallsApps?: AppBreakdown[];
    totalCallsApps?: AppBreakdown[];
    containableCallsApps?: AppBreakdown[];
    ordersPlacedApps?: AppBreakdown[];
    orderAttemptsApps?: AppBreakdown[];
    upsellApps?: AppBreakdown[];
    ticketApps?: AppBreakdown[];
    questionsApps?: AppBreakdown[];
    // Additional fields
    [key: string]: any;
}

/**
 * Filter for engagement analytics requests
 */
export interface EngagementAnalyticsFilter {
    startDate: string;
    endDate: string;
    skip?: number;
    take?: number;
    interval?: number;
    applicationIds?: string[];  // Optional filter by specific application IDs
    timezone?: string;
    signal?: AbortSignal;
}

export interface DeepAnalysisTagCount {
    tag: string;
    count: number;
}

export interface DeepAnalysisUpsellValueTotal {
    'upsold-item': string;
    'total-dollar-value': number;
}

export interface DeepAnalysisUpsellSession {
    applicationId: string;
    sessionId: string;
    callTime: string;
}

export interface DeepAnalysisUpsellSummary {
    tagsTotals: DeepAnalysisTagCount[];
    upsellValues: DeepAnalysisUpsellValueTotal[];
    byDayOfWeek: Record<string, DeepAnalysisTagCount[]>;
    byHourOfDay: Record<string, DeepAnalysisTagCount[]>;
    byPeriod: Record<string, DeepAnalysisTagCount[]>;
    withSessionId: Record<string, DeepAnalysisUpsellSession[]>;
    periodGranularity: string;
    sessionCount: number;
    taggedSessionCount: number;
}

export type DeepAnalysisUpsellSummaryByApplication = Record<string, DeepAnalysisUpsellSummary>;

/**
 * Transfer reason data from the API
 * Maps to TransferCategoryData structure for the transfers chart
 */
export interface TransferReasonData {
    reason: string;           // Transfer reason code/key
    reasonLabel?: string;     // Human-readable label
    count: number;
    percentage?: number;
    isPlanned: boolean;       // true = Planned, false = Unplanned
    // App-level breakdown
    apps?: AppBreakdown[];
}

/**
 * Individual transfer record for the transfers table/export
 * Maps to mockHealthcareTransferData / mockFoodOrderingTransferData
 */
export interface TransferRecord {
    id: string;
    date: string;
    time: string;
    phoneLastFour: string;
    transcriptLink: string;
    transferType: 'Planned' | 'Unplanned';
    transferReason: string;
    transferReasonLabel?: string;
    location: string;
    applicationId?: string;
}

/**
 * Aggregated transfer reasons response
 */
export interface TransferReasonsResponse {
    planned: TransferReasonData[];
    unplanned: TransferReasonData[];
    totalPlanned: number;
    totalUnplanned: number;
    totalTransfers: number;
}

/**
 * Contained calls data by category (for bar charts)
 * Maps to ContainedCallData structure
 */
export interface ContainedCallsData {
    category: string;        // e.g., 'Schedule', 'Reschedule', 'Cancel', 'FAQ'
    containedCount: number;
    notContainedCount: number;
    containedApps?: AppBreakdown[];
    notContainedApps?: AppBreakdown[];
}

const buildEngagementDateFilter = (filter: EngagementAnalyticsFilter) => {
    // Pass dates as-is - server will handle timezone conversion using the timezone parameter
    const startDate = encodeURIComponent(filter.startDate);
    const endDate = encodeURIComponent(filter.endDate);
    let queryString = `startDate=${startDate}&endDate=${endDate}`;

    // Include timezone if provided (from Intl.DateTimeFormat().resolvedOptions().timeZone)
    if (filter.timezone) {
        queryString += `&timezone=${encodeURIComponent(filter.timezone)}`;
    }

    return queryString;
};

// #region Engagements

export const getApplicationEngagements = (
    applicationId: string,
    filter: EngagementAnalyticsFilter
): Promise<IResult<OrganizationEngagement[]>> => {
    const skip = filter.skip ?? 0;
    const take = filter.take ?? 100;
    return sendGet<OrganizationEngagement[]>(
        `/v2/Application/${applicationId}/engagements?${buildEngagementDateFilter(filter)}&skip=${skip}&take=${take}`,
        getAnalyticsApiUrl(), filter?.signal
    );
};

// this stops working after 10k
export const getOrganizationEngagementsNotWorkingElasticsearch = (
    organizationId: string,
    filter: EngagementAnalyticsFilter
): Promise<IResult<GroupedEngagements>> => {
    const skip = filter.skip ?? 0;
    const take = filter.take ?? 100;
    return sendGet<GroupedEngagements>(
        `/EngagementAnalyticsReporting/getOrganizationEngagements?organizationId=${organizationId}&${buildEngagementDateFilter(filter)}&skip=${skip}&take=${take}`,
        getAnalyticsApiUrl(), filter?.signal
    );
};
export const getOrganizationEngagements = (
    organizationId: string,
    filter: EngagementAnalyticsFilter
): Promise<IResult<OrganizationEngagement[]>> => {
    const skip = filter.skip ?? 0;
    const take = filter.take ?? 100;
    return sendGet<OrganizationEngagement[]>(
        `/v2/Organization/${organizationId}/engagements?${buildEngagementDateFilter(filter)}&skip=${skip}&take=${take}`,
        getAnalyticsApiUrl(), filter?.signal
    );
};


export const getEngagementsForTagCombo = (
    organizationId: string,
    filter: EngagementAnalyticsFilter,
    category: string,
    validTags: string[] = [],
    excludedTags: string[] = []
): Promise<IResult<GroupedEngagements>> => {
    const validTagsQuery = validTags.map(t => `validTags=${encodeURIComponent(t)}`).join('&');
    const excludedTagsQuery = excludedTags.map(t => `excludedTags=${encodeURIComponent(t)}`).join('&');
    let url = `/EngagementAnalyticsReporting/getEngagementsForTagCombo?organizationId=${organizationId}&${buildEngagementDateFilter(filter)}&category=${encodeURIComponent(category)}`;
    if (validTagsQuery) url += `&${validTagsQuery}`;
    if (excludedTagsQuery) url += `&${excludedTagsQuery}`;
    return sendGet<GroupedEngagements>(url, getAnalyticsApiUrl(), filter?.signal);
};

export const getPlatformEngagements = (
    organizationIds: string[],
    filter: EngagementAnalyticsFilter
): Promise<IResult<GroupedEngagements>> => {
    const organizationIdsQuery = organizationIds.map(id => `organizationIds=${encodeURIComponent(id)}`).join('&');
    const skip = filter.skip ?? 0;
    const take = filter.take ?? 5000;
    return sendGet<GroupedEngagements>(
        `/EngagementAnalyticsReporting/getPlatformEngagements?${organizationIdsQuery}&${buildEngagementDateFilter(filter)}&skip=${skip}&take=${take}`,
        getAnalyticsApiUrl(), filter?.signal
    );
};

export const getPlatformEngagementsForTagCombo = (
    organizationIds: string[],
    filter: EngagementAnalyticsFilter,
    category: string,
    validTags: string[] = [],
    excludedTags: string[] = []
): Promise<IResult<GroupedEngagements>> => {
    const organizationIdsQuery = organizationIds.map(id => `organizationIds=${encodeURIComponent(id)}`).join('&');
    const validTagsQuery = validTags.map(t => `validTags=${encodeURIComponent(t)}`).join('&');
    const excludedTagsQuery = excludedTags.map(t => `excludedTags=${encodeURIComponent(t)}`).join('&');
    let url = `/EngagementAnalyticsReporting/getPlatformEngagementsForTagCombo?${organizationIdsQuery}&${buildEngagementDateFilter(filter)}&category=${encodeURIComponent(category)}`;
    if (validTagsQuery) url += `&${validTagsQuery}`;
    if (excludedTagsQuery) url += `&${excludedTagsQuery}`;
    return sendGet<GroupedEngagements>(url, getAnalyticsApiUrl(), filter?.signal);
};

// #endregion

// #region Top Level Metrics

export const getTopLevelMetrics = (
    applicationId: string,
    filter: EngagementAnalyticsFilter
): Promise<IResult<TopLevelMetrics>> => {
    return sendGet<TopLevelMetrics>(
        `/EngagementAnalyticsReporting/getTopLevelMetrics?applicationId=${applicationId}&${buildEngagementDateFilter(filter)}`,
        getAnalyticsApiUrl(), filter?.signal
    );
};

export const getOrganizationTopLevelMetrics = (
    organizationId: string,
    filter: EngagementAnalyticsFilter
): Promise<IResult<GroupedTopLevelMetrics>> => {
    return sendGet<GroupedTopLevelMetrics>(
        `/EngagementAnalyticsReporting/getOrganizationTopLevelMetrics?organizationId=${organizationId}&${buildEngagementDateFilter(filter)}`,
        getAnalyticsApiUrl(), filter?.signal
    );
};

export const getDeepAnalysisUpsellSummary = (
    organizationId: string,
    filter: EngagementAnalyticsFilter,
    groupId: string = 'upsell-success'
): Promise<IResult<DeepAnalysisUpsellSummaryByApplication>> => {
    return sendGet<DeepAnalysisUpsellSummaryByApplication>(
        `/EngagementAnalyticsReporting/getDeepAnalysisUpsellSummary?organizationId=${encodeURIComponent(organizationId)}&${buildEngagementDateFilter(filter)}&groupId=${encodeURIComponent(groupId)}`,
        getAnalyticsApiUrl(), filter?.signal
    );
};

export const getPlatformTopLevelMetrics = (
    organizationIds: string[],
    filter: EngagementAnalyticsFilter
): Promise<IResult<GroupedTopLevelMetrics>> => {
    const organizationIdsQuery = organizationIds.map(id => `organizationIds=${encodeURIComponent(id)}`).join('&');
    return sendGet<GroupedTopLevelMetrics>(
        `/EngagementAnalyticsReporting/getPlatformTopLevelMetrics?${organizationIdsQuery}&${buildEngagementDateFilter(filter)}`,
        getAnalyticsApiUrl(), filter?.signal
    );
};

// #endregion

// #region Upsold Revenue

export const getPlatformUpsoldRevenue = (
    organizationIds: string[],
    filter: EngagementAnalyticsFilter
): Promise<IResult<Record<string, number>>> => {
    const organizationIdsQuery = organizationIds.map(id => `organizationIds=${encodeURIComponent(id)}`).join('&');
    return sendGet<Record<string, number>>(
        `/EngagementAnalyticsReporting/getPlatformUpsoldRevenue?${organizationIdsQuery}&${buildEngagementDateFilter(filter)}`,
        getAnalyticsApiUrl(), filter?.signal
    );
};

// #endregion

// #region Average Call Volume Histograms

export const getAverageCallVolumeHistogram = (
    applicationId: string,
    filter: EngagementAnalyticsFilter
): Promise<IResult<HistogramModel>> => {
    const interval = filter.interval ?? 0;
    return sendGet<HistogramModel>(
        `/EngagementAnalyticsReporting/getAverageCallVolumeHistogram?applicationId=${applicationId}&${buildEngagementDateFilter(filter)}&interval=${interval}`,
        getAnalyticsApiUrl(), filter?.signal
    );
};

export const getOrganizationAverageCallVolumeHistogram = (
    organizationId: string,
    filter: EngagementAnalyticsFilter
): Promise<IResult<GroupedHistogramData>> => {
    const interval = filter.interval ?? 0;
    return sendGet<GroupedHistogramData>(
        `/EngagementAnalyticsReporting/getOrganizationAverageCallVolumeHistogram?organizationId=${organizationId}&${buildEngagementDateFilter(filter)}&interval=${interval}`,
        getAnalyticsApiUrl(), filter?.signal
    );
};

export const getPlatformAverageCallVolumeHistogram = (
    organizationIds: string[],
    filter: EngagementAnalyticsFilter
): Promise<IResult<GroupedHistogramData>> => {
    const interval = filter.interval ?? 0;
    const organizationIdsQuery = organizationIds.map(id => `organizationIds=${encodeURIComponent(id)}`).join('&');
    return sendGet<GroupedHistogramData>(
        `/EngagementAnalyticsReporting/getPlatformAverageCallVolumeHistogram?${organizationIdsQuery}&${buildEngagementDateFilter(filter)}&interval=${interval}`,
        getAnalyticsApiUrl(), filter?.signal
    );
};

export const getAverageContainableCallVolumeHistogram = (
    applicationId: string,
    filter: EngagementAnalyticsFilter
): Promise<IResult<HistogramModel>> => {
    const interval = filter.interval ?? 0;
    return sendGet<HistogramModel>(
        `/EngagementAnalyticsReporting/getAverageContainableCallVolumeHistogram?applicationId=${applicationId}&${buildEngagementDateFilter(filter)}&interval=${interval}`,
        getAnalyticsApiUrl(), filter?.signal
    );
};

export const getOrganizationAverageContainableCallVolumeHistogram = (
    organizationId: string,
    filter: EngagementAnalyticsFilter
): Promise<IResult<GroupedHistogramData>> => {
    const interval = filter.interval ?? 0;
    return sendGet<GroupedHistogramData>(
        `/EngagementAnalyticsReporting/getOrganizationAverageContainableCallVolumeHistogram?organizationId=${organizationId}&${buildEngagementDateFilter(filter)}&interval=${interval}`,
        getAnalyticsApiUrl(), filter?.signal
    );
};

export const getAverageContainedCallVolumeHistogram = (
    applicationId: string,
    filter: EngagementAnalyticsFilter
): Promise<IResult<HistogramModel>> => {
    const interval = filter.interval ?? 0;
    return sendGet<HistogramModel>(
        `/EngagementAnalyticsReporting/getAverageContainedCallVolumeHistogram?applicationId=${applicationId}&${buildEngagementDateFilter(filter)}&interval=${interval}`,
        getAnalyticsApiUrl(), filter?.signal
    );
};

export const getOrganizationAverageContainedCallVolumeHistogram = (
    organizationId: string,
    filter: EngagementAnalyticsFilter
): Promise<IResult<GroupedHistogramData>> => {
    const interval = filter.interval ?? 0;
    return sendGet<GroupedHistogramData>(
        `/EngagementAnalyticsReporting/getOrganizationAverageContainedCallVolumeHistogram?organizationId=${organizationId}&${buildEngagementDateFilter(filter)}&interval=${interval}`,
        getAnalyticsApiUrl(), filter?.signal
    );
};

// #endregion

// #region Call Volume Histograms

export const getCallVolumeHistogram = (
    applicationId: string,
    filter: EngagementAnalyticsFilter
): Promise<IResult<HistogramModel>> => {
    const interval = filter.interval ?? 0;
    return sendGet<HistogramModel>(
        `/EngagementAnalyticsReporting/getCallVolumeHistogram?applicationId=${applicationId}&${buildEngagementDateFilter(filter)}&interval=${interval}`,
        getAnalyticsApiUrl(), filter?.signal
    );
};

export const getOrganizationCallVolumeHistogram = (
    organizationId: string,
    filter: EngagementAnalyticsFilter
): Promise<IResult<GroupedHistogramData>> => {
    const interval = filter.interval ?? 0;
    return sendGet<GroupedHistogramData>(
        `/EngagementAnalyticsReporting/getOrganizationCallVolumeHistogram?organizationId=${organizationId}&${buildEngagementDateFilter(filter)}&interval=${interval}`,
        getAnalyticsApiUrl(), filter?.signal
    );
};

export const getPlatformCallVolumeHistogram = (
    organizationIds: string[],
    filter: EngagementAnalyticsFilter
): Promise<IResult<GroupedHistogramData>> => {
    const interval = filter.interval ?? 0;
    const organizationIdsQuery = organizationIds.map(id => `organizationIds=${encodeURIComponent(id)}`).join('&');
    return sendGet<GroupedHistogramData>(
        `/EngagementAnalyticsReporting/getPlatformCallVolumeHistogram?${organizationIdsQuery}&${buildEngagementDateFilter(filter)}&interval=${interval}`,
        getAnalyticsApiUrl(), filter?.signal
    );
};

export const getContainableCallVolumeHistogram = (
    applicationId: string,
    filter: EngagementAnalyticsFilter
): Promise<IResult<HistogramModel>> => {
    const interval = filter.interval ?? 0;
    return sendGet<HistogramModel>(
        `/EngagementAnalyticsReporting/getContainableCallVolumeHistogram?applicationId=${applicationId}&${buildEngagementDateFilter(filter)}&interval=${interval}`,
        getAnalyticsApiUrl(), filter?.signal
    );
};

export const getOrganizationContainableCallVolumeHistogram = (
    organizationId: string,
    filter: EngagementAnalyticsFilter
): Promise<IResult<GroupedHistogramData>> => {
    const interval = filter.interval ?? 0;
    return sendGet<GroupedHistogramData>(
        `/EngagementAnalyticsReporting/getOrganizationContainableCallVolumeHistogram?organizationId=${organizationId}&${buildEngagementDateFilter(filter)}&interval=${interval}`,
        getAnalyticsApiUrl(), filter?.signal
    );
};

export const getPlatformContainableCallVolumeHistogram = (
    organizationIds: string[],
    filter: EngagementAnalyticsFilter
): Promise<IResult<GroupedHistogramData>> => {
    const interval = filter.interval ?? 0;
    const organizationIdsQuery = organizationIds.map(id => `organizationIds=${encodeURIComponent(id)}`).join('&');
    return sendGet<GroupedHistogramData>(
        `/EngagementAnalyticsReporting/getPlatformContainableCallVolumeHistogram?${organizationIdsQuery}&${buildEngagementDateFilter(filter)}&interval=${interval}`,
        getAnalyticsApiUrl(), filter?.signal
    );
};

export const getContainedCallVolumeHistogram = (
    applicationId: string,
    filter: EngagementAnalyticsFilter
): Promise<IResult<HistogramModel>> => {
    const interval = filter.interval ?? 0;
    return sendGet<HistogramModel>(
        `/EngagementAnalyticsReporting/getContainedCallVolumeHistogram?applicationId=${applicationId}&${buildEngagementDateFilter(filter)}&interval=${interval}`,
        getAnalyticsApiUrl(), filter?.signal
    );
};

export const getOrganizationContainedCallVolumeHistogram = (
    organizationId: string,
    filter: EngagementAnalyticsFilter
): Promise<IResult<GroupedHistogramData>> => {
    const interval = filter.interval ?? 0;
    return sendGet<GroupedHistogramData>(
        `/EngagementAnalyticsReporting/getOrganizationContainedCallVolumeHistogram?organizationId=${organizationId}&${buildEngagementDateFilter(filter)}&interval=${interval}`,
        getAnalyticsApiUrl(), filter?.signal
    );
};

export const getPlatformContainedCallVolumeHistogram = (
    organizationIds: string[],
    filter: EngagementAnalyticsFilter
): Promise<IResult<GroupedHistogramData>> => {
    const interval = filter.interval ?? 0;
    const organizationIdsQuery = organizationIds.map(id => `organizationIds=${encodeURIComponent(id)}`).join('&');
    return sendGet<GroupedHistogramData>(
        `/EngagementAnalyticsReporting/getPlatformContainedCallVolumeHistogram?${organizationIdsQuery}&${buildEngagementDateFilter(filter)}&interval=${interval}`,
        getAnalyticsApiUrl(), filter?.signal
    );
};

// #endregion

// #region Order Volume Histograms

export const getOrderAttemptedVolume = (
    applicationId: string,
    filter: EngagementAnalyticsFilter
): Promise<IResult<HistogramModel>> => {
    const interval = filter.interval ?? 0;
    return sendGet<HistogramModel>(
        `/EngagementAnalyticsReporting/getOrderAttemptedVolume?applicationId=${applicationId}&${buildEngagementDateFilter(filter)}&interval=${interval}`,
        getAnalyticsApiUrl(), filter?.signal
    );
};

export const getOrganizationOrderAttemptedVolume = (
    organizationId: string,
    filter: EngagementAnalyticsFilter
): Promise<IResult<GroupedHistogramData>> => {
    const interval = filter.interval ?? 0;
    return sendGet<GroupedHistogramData>(
        `/EngagementAnalyticsReporting/getOrganizationOrderAttemptedVolume?organizationId=${organizationId}&${buildEngagementDateFilter(filter)}&interval=${interval}`,
        getAnalyticsApiUrl(), filter?.signal
    );
};

export const getPlatformOrderAttemptedVolume = (
    organizationIds: string[],
    filter: EngagementAnalyticsFilter
): Promise<IResult<GroupedHistogramData>> => {
    const interval = filter.interval ?? 0;
    const organizationIdsQuery = organizationIds.map(id => `organizationIds=${encodeURIComponent(id)}`).join('&');
    return sendGet<GroupedHistogramData>(
        `/EngagementAnalyticsReporting/getPlatformOrderAttemptedVolume?${organizationIdsQuery}&${buildEngagementDateFilter(filter)}&interval=${interval}`,
        getAnalyticsApiUrl(), filter?.signal
    );
};

export const getOrderPlacedVolume = (
    applicationId: string,
    filter: EngagementAnalyticsFilter
): Promise<IResult<HistogramModel>> => {
    const interval = filter.interval ?? 0;
    return sendGet<HistogramModel>(
        `/EngagementAnalyticsReporting/getOrderPlacedVolume?applicationId=${applicationId}&${buildEngagementDateFilter(filter)}&interval=${interval}`,
        getAnalyticsApiUrl(), filter?.signal
    );
};

export const getOrganizationOrderPlacedVolume = (
    organizationId: string,
    filter: EngagementAnalyticsFilter
): Promise<IResult<GroupedHistogramData>> => {
    const interval = filter.interval ?? 0;
    return sendGet<GroupedHistogramData>(
        `/EngagementAnalyticsReporting/getOrganizationOrderPlacedVolume?organizationId=${organizationId}&${buildEngagementDateFilter(filter)}&interval=${interval}`,
        getAnalyticsApiUrl(), filter?.signal
    );
};

export const getPlatformOrderPlacedVolume = (
    organizationIds: string[],
    filter: EngagementAnalyticsFilter
): Promise<IResult<GroupedHistogramData>> => {
    const interval = filter.interval ?? 0;
    const organizationIdsQuery = organizationIds.map(id => `organizationIds=${encodeURIComponent(id)}`).join('&');
    return sendGet<GroupedHistogramData>(
        `/EngagementAnalyticsReporting/getPlatformOrderPlacedVolume?${organizationIdsQuery}&${buildEngagementDateFilter(filter)}&interval=${interval}`,
        getAnalyticsApiUrl(), filter?.signal
    );
};

// #endregion

// #region Spanish Call Volume Histograms

export const getSpanishCallVolumeHistogram = (
    applicationId: string,
    filter: EngagementAnalyticsFilter
): Promise<IResult<HistogramModel>> => {
    const interval = filter.interval ?? 0;
    return sendGet<HistogramModel>(
        `/EngagementAnalyticsReporting/getSpanishCallVolumeHistogram?applicationId=${applicationId}&${buildEngagementDateFilter(filter)}&interval=${interval}`,
        getAnalyticsApiUrl(), filter?.signal
    );
};

export const getOrganizationSpanishCallVolumeHistogram = (
    organizationId: string,
    filter: EngagementAnalyticsFilter
): Promise<IResult<GroupedHistogramData>> => {
    const interval = filter.interval ?? 0;
    return sendGet<GroupedHistogramData>(
        `/EngagementAnalyticsReporting/getOrganizationSpanishCallVolumeHistogram?organizationId=${organizationId}&${buildEngagementDateFilter(filter)}&interval=${interval}`,
        getAnalyticsApiUrl(), filter?.signal
    );
};

export const getPlatformSpanishCallVolumeHistogram = (
    organizationIds: string[],
    filter: EngagementAnalyticsFilter
): Promise<IResult<GroupedHistogramData>> => {
    const interval = filter.interval ?? 0;
    const organizationIdsQuery = organizationIds.map(id => `organizationIds=${encodeURIComponent(id)}`).join('&');
    return sendGet<GroupedHistogramData>(
        `/EngagementAnalyticsReporting/getPlatformSpanishCallVolumeHistogram?${organizationIdsQuery}&${buildEngagementDateFilter(filter)}&interval=${interval}`,
        getAnalyticsApiUrl(), filter?.signal
    );
};

export const getSpanishContainableCallVolumeHistogram = (
    applicationId: string,
    filter: EngagementAnalyticsFilter
): Promise<IResult<HistogramModel>> => {
    const interval = filter.interval ?? 0;
    return sendGet<HistogramModel>(
        `/EngagementAnalyticsReporting/getSpanishContainableCallVolumeHistogram?applicationId=${applicationId}&${buildEngagementDateFilter(filter)}&interval=${interval}`,
        getAnalyticsApiUrl(), filter?.signal
    );
};

export const getOrganizationSpanishContainableCallVolumeHistogram = (
    organizationId: string,
    filter: EngagementAnalyticsFilter
): Promise<IResult<GroupedHistogramData>> => {
    const interval = filter.interval ?? 0;
    return sendGet<GroupedHistogramData>(
        `/EngagementAnalyticsReporting/getOrganizationSpanishContainableCallVolumeHistogram?organizationId=${organizationId}&${buildEngagementDateFilter(filter)}&interval=${interval}`,
        getAnalyticsApiUrl(), filter?.signal
    );
};

export const getPlatformSpanishContainableCallVolumeHistogram = (
    organizationIds: string[],
    filter: EngagementAnalyticsFilter
): Promise<IResult<GroupedHistogramData>> => {
    const interval = filter.interval ?? 0;
    const organizationIdsQuery = organizationIds.map(id => `organizationIds=${encodeURIComponent(id)}`).join('&');
    return sendGet<GroupedHistogramData>(
        `/EngagementAnalyticsReporting/getPlatformSpanishContainableCallVolumeHistogram?${organizationIdsQuery}&${buildEngagementDateFilter(filter)}&interval=${interval}`,
        getAnalyticsApiUrl(), filter?.signal
    );
};

export const getSpanishContainedCallVolumeHistogram = (
    applicationId: string,
    filter: EngagementAnalyticsFilter
): Promise<IResult<HistogramModel>> => {
    const interval = filter.interval ?? 0;
    return sendGet<HistogramModel>(
        `/EngagementAnalyticsReporting/getSpanishContainedCallVolumeHistogram?applicationId=${applicationId}&${buildEngagementDateFilter(filter)}&interval=${interval}`,
        getAnalyticsApiUrl(), filter?.signal
    );
};

export const getOrganizationSpanishContainedCallVolumeHistogram = (
    organizationId: string,
    filter: EngagementAnalyticsFilter
): Promise<IResult<GroupedHistogramData>> => {
    const interval = filter.interval ?? 0;
    return sendGet<GroupedHistogramData>(
        `/EngagementAnalyticsReporting/getOrganizationSpanishContainedCallVolumeHistogram?organizationId=${organizationId}&${buildEngagementDateFilter(filter)}&interval=${interval}`,
        getAnalyticsApiUrl(), filter?.signal
    );
};

export const getPlatformSpanishContainedCallVolumeHistogram = (
    organizationIds: string[],
    filter: EngagementAnalyticsFilter
): Promise<IResult<GroupedHistogramData>> => {
    const interval = filter.interval ?? 0;
    const organizationIdsQuery = organizationIds.map(id => `organizationIds=${encodeURIComponent(id)}`).join('&');
    return sendGet<GroupedHistogramData>(
        `/EngagementAnalyticsReporting/getPlatformSpanishContainedCallVolumeHistogram?${organizationIdsQuery}&${buildEngagementDateFilter(filter)}&interval=${interval}`,
        getAnalyticsApiUrl(), filter?.signal
    );
};

// #endregion

// #region Concurrent Call Volume Histograms

export const getConcurrentCallVolumeHistogram = (
    applicationId: string,
    filter: EngagementAnalyticsFilter
): Promise<IResult<HistogramModel>> => {
    const interval = filter.interval ?? 0;
    return sendGet<HistogramModel>(
        `/EngagementAnalyticsReporting/getConcurrentCallVolumeHistogram?applicationId=${applicationId}&${buildEngagementDateFilter(filter)}&interval=${interval}`,
        getAnalyticsApiUrl(), filter?.signal
    );
};

export const getOrganizationConcurrentCallVolumeHistogram = (
    organizationId: string,
    filter: EngagementAnalyticsFilter
): Promise<IResult<GroupedHistogramData>> => {
    const interval = filter.interval ?? 0;
    return sendGet<GroupedHistogramData>(
        `/EngagementAnalyticsReporting/getOrganizationConcurrentCallVolumeHistogram?organizationId=${organizationId}&${buildEngagementDateFilter(filter)}&interval=${interval}`,
        getAnalyticsApiUrl(), filter?.signal
    );
};

export const getPlatformConcurrentCallVolumeHistogram = (
    organizationIds: string[],
    filter: EngagementAnalyticsFilter
): Promise<IResult<GroupedHistogramData>> => {
    const interval = filter.interval ?? 0;
    const organizationIdsQuery = organizationIds.map(id => `organizationIds=${encodeURIComponent(id)}`).join('&');
    return sendGet<GroupedHistogramData>(
        `/EngagementAnalyticsReporting/getPlatformConcurrentCallVolumeHistogram?${organizationIdsQuery}&${buildEngagementDateFilter(filter)}&interval=${interval}`,
        getAnalyticsApiUrl(), filter?.signal
    );
};

// #endregion

// #region Transfer Reasons

/**
 * API response format for getOrganizationLevelTransferReasons
 * Returns a record of applicationId -> (transferReason -> count)
 * Example: { "app-id-1": { "CUSTOMER_FRUSTRATED": 1, "UNKNOWN": 0 }, "app-id-2": { "UNKNOWN": 0 } }
 */
export type TransferReasonsApiResponse = Record<string, Record<string, number>>;

/**
 * @deprecated Use TransferReasonsApiResponse instead
 * Keeping for backward compatibility with OrgAnalyticsContainer
 */
export interface LegacyTransferReason {
    reason?: string;
    count?: number;
    percentage?: number;
    isPlanned?: boolean;
    apps?: AppBreakdown[];
    [key: string]: any;
}

export const getOrganizationLevelTransferReasons = (
    organizationId: string,
    filter: EngagementAnalyticsFilter,
    getPlanned: boolean = false
): Promise<IResult<TransferReasonsApiResponse>> => {
    return sendGet<TransferReasonsApiResponse>(
        `/EngagementAnalyticsReporting/getOrganizationLevelTransferReasons?organizationId=${organizationId}&${buildEngagementDateFilter(filter)}&getPlanned=${getPlanned}`,
        getAnalyticsApiUrl(), filter?.signal
    );
};

export const getPlatformTransferReasons = (
    organizationIds: string[],
    filter: EngagementAnalyticsFilter,
    getPlanned: boolean = false
): Promise<IResult<TransferReasonsApiResponse>> => {
    const organizationIdsQuery = organizationIds.map(id => `organizationIds=${encodeURIComponent(id)}`).join('&');
    return sendGet<TransferReasonsApiResponse>(
        `/EngagementAnalyticsReporting/getPlatformTransferReasons?${organizationIdsQuery}&${buildEngagementDateFilter(filter)}&getPlanned=${getPlanned}`,
        getAnalyticsApiUrl(), filter?.signal
    );
};

export const getApplicationLevelTransferReasons = (
    applicationId: string,
    filter: EngagementAnalyticsFilter,
    getPlanned: boolean = false
): Promise<IResult<Record<string, number>>> => {
    return sendGet<Record<string, number>>(
        `/EngagementAnalyticsReporting/getApplicationLevelTransferReasons?applicationId=${applicationId}&${buildEngagementDateFilter(filter)}&getPlanned=${getPlanned}`,
        getAnalyticsApiUrl(), filter?.signal
    );
};

// #endregion
